import { Avatar, Box, CircularProgress, Grid, Paper, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUILoadingLinear } from '../../../../atomic/atoms';
import { CustomerQuery } from '../../../../services/query';
import { useLocation } from 'react-router';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { Table, TableHead, TableRow, TableCell, TableBody, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Pagination from '@mui/material/Pagination';
import React from 'react';
import AccordionSummary from '@mui/material/AccordionSummary';
import { useSelector } from 'react-redux';
import { NoResultFoundComponent } from '../NoResultFoundComponent/NoResultFoundComponent';
import IconButton from '@mui/material/IconButton';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Swal from 'sweetalert2';
import { CRT, crtCls } from './customerReturn.classes';

const appendCopiedText = (prevList: any[], text: any) => [...prevList, text];

const withoutCopiedText = (prevList: any[], text: any) => prevList.filter((item) => item !== text);

const clearCopiedTextAfterDelay = (
    text: any,
    setCopiedList: React.Dispatch<React.SetStateAction<any[]>>,
    delayMs = 5000
) => {
    window.setTimeout(() => {
        setCopiedList((prevList) => withoutCopiedText(prevList, text));
    }, delayMs);
};

export const CustomerReturn: FC<any> = ({ datas }) => {
    const pageSize = 10;
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [loadingCollapse, setLoadingCollapse] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [rowCount, setRowCount] = useState(0);
    const [fetchDatas, setFetchDatas] = useState<ICustomerReturn[]>([]);
    const [expandedIndex, setExpandedIndex] = useState<number[]>([]);
    const [copiedList, setCopiedList] = useState<any[]>([]);

    const search = useLocation().search;
    const searchParams = new URLSearchParams(search);
    const darkModeStore: any = useSelector((state: any) => state?.darkMode);
    interface ICustomerReturn {
        customerId: string | number;
        orderId: string | number;
        orderNumber: string | number;
        returnDate: string;
        returnId: number;
        rmaCode: string;
        shippingListNo: string;
        status: string;
        statusDescription: string;
        details?: any[];
    }
    const handleAccordionToggle = (index: number) => {
        if (expandedIndex.includes(index)) {
            setExpandedIndex(expandedIndex.filter((i) => i !== index));
        } else {
            setExpandedIndex([...expandedIndex, index]);
        }
    };
    const handleCopyClick = (event: React.MouseEvent, text: any) => {
        event.stopPropagation();
        copyToClipboard(text);
        setCopiedList((prevList) => appendCopiedText(prevList, text));
        clearCopiedTextAfterDelay(text, setCopiedList);
    };
    const copyToClipboard = async (text: any) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // do nothing
        }
    };

    const handlePageNumber = (i: any) => {
        setPageNumber(Number(i.currentTarget.textContent));
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'customerId' as keyof ICustomerReturn,
            headerName: 'customerId',
            flex: 1,
            hide: true,
        },
        {
            field: 'orderId',
            headerName: t('customerInformation.orderId'),
            flex: 1,
            hide: true,
        },
        {
            field: 'orderNumber',
            headerName: t('customerInformation.orderNumber'),
            flex: 1,
        },
        {
            field: 'returnId',
            headerName: t('customerInformation.returnId'),
            flex: 1,
        },
        {
            field: 'rmaCode',
            headerName: t('customerInformation.rmaCode'),
            flex: 1,
        },
        {
            field: 'shippingListNo',
            headerName: t('customerInformation.shippingListNo'),
            flex: 1,
        },

        {
            field: 'statusDescription',
            headerName: t('customerInformation.statusDescription'),
            flex: 1,
        },
    ];

    const fetchData = () => {
        setLoading(true);
        let customerIdValue;
        if (!datas) {
            customerIdValue = Number(searchParams.get('customerId')) || Number(searchParams.get('CustomerId'));
        } else {
            customerIdValue = datas.customer.customerId;
        }

        const requestData = {
            customerId: customerIdValue,
            take: pageSize,
            skip: pageNumber - 1,
        };
        CustomerQuery.GetCustomerReturnList(requestData)
            .then((res: any) => {
                if (res.data) {
                    let parseData = res.data.map((item: any) => {
                        return {
                            customerId: item.customerId || '-',
                            orderId: item.orderId || '-',
                            orderNumber: item.orderNumber || '-',
                            returnDate: item.returnDate || '-',
                            returnId: item.returnId,
                            rmaCode: item.rmaCode || '-',
                            shippingListNo: item.shippingListNo || '-',
                            status: item.status,
                            statusDescription: item.statusDescription,
                            details: null,
                        };
                    });
                    setFetchDatas(parseData);
                    setRowCount(res.count);
                }
            })
            .catch(() => {
                Swal.fire(t('general.error'), ' ', 'error');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const getReturnDetail = (row: any, index: number) => {
        if (expandedIndex.includes(index)) return;
        setLoadingCollapse(true);
        const requestData = { customerId: row.customerId, returnId: row.returnId };
        CustomerQuery.GetReturnDetailByReturnId(requestData)
            .then((res: any) => {
                if (res.data.productDetailModels) {
                    let parseData = res.data.productDetailModels.map((item: any) => {
                        return {
                            barcode: item.barcode || '-',
                            brandName: item.brandName || '-',
                            isReturnable: item.isReturnable || '-',
                            productImageUrl: item.productImageUrl || '-',
                            reasonDescription: item.reasonDescription,
                            returnItemStatusKey: item.returnItemStatusKey || '-',
                            size: item.size || '-',
                            renkKod: item.renkKod,
                            ozelKod1: item.ozelKod1,
                            productId: item.productId,
                            salePrice: item.salePrice,
                            shippingListId: item.shippingListId,
                        };
                    });
                    const updatedFetchDatas = [...fetchDatas];
                    const indexToUpdate = updatedFetchDatas.findIndex((item) => item.returnId === row.returnId);
                    if (indexToUpdate !== -1) {
                        updatedFetchDatas[indexToUpdate].details = parseData;
                    }
                    setFetchDatas(updatedFetchDatas);
                }
            })
            .catch(() => {
                Swal.fire(t('general.error'), ' ', 'error');
            })
            .finally(() => {
                setLoadingCollapse(false);
            });
    };

    useEffect(() => {
        fetchData();
    }, [pageNumber]);

    return (
        <Paper className={CRT.page}>
            {fetchDatas &&
                fetchDatas.map((row, index) => (
                    <Accordion
                        className={CRT.accordion}
                        key={index}
                        expanded={expandedIndex.includes(index)}
                        style={{
                            backgroundColor: !darkModeStore?.isDarkMode ? '#FDFBF4' : '#1e1e1e',
                            marginTop: '2rem',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                        }}
                        onChange={() => {
                            handleAccordionToggle(index);
                        }}
                    >
                        <AccordionSummary
                            className={CRT.accordionSummary}
                            expandIcon={<ExpandMoreIcon className={CRT.iconAccordionExpand} />}
                            onClick={() => getReturnDetail(row, index)}
                            sx={{
                                width: '100%',
                                padding: '12px',
                                '& .MuiAccordionSummary-expandIconWrapper': { marginLeft: '12px' },
                            }}
                        >
                            <Table className={CRT.summaryTable} style={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow>
                                        {columns.map(
                                            (column, colIndex) =>
                                                !column.hide && (
                                                    <TableCell
                                                        key={colIndex}
                                                        style={{
                                                            minWidth: column.minWidth,
                                                            maxWidth: column.maxWidth,
                                                            width: column.width || 'auto',
                                                        }}
                                                    >
                                                        <h4>{column.headerName}</h4>
                                                    </TableCell>
                                                )
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        {columns.map(
                                            (column, colIndex) =>
                                                !column.hide && (
                                                    <TableCell
                                                        key={colIndex}
                                                        style={{
                                                            minWidth: column.minWidth,
                                                            maxWidth: column.maxWidth,
                                                            width: column.width || 'auto',
                                                        }}
                                                    >
                                                        {row[column.field as keyof ICustomerReturn]}
                                                        {colIndex === 2 && (
                                                            <IconButton
                                                                className={CRT.inputIconButtonCopyField}
                                                                onClick={(event) =>
                                                                    handleCopyClick(
                                                                        event,
                                                                        row[column.field as keyof ICustomerReturn]
                                                                    )
                                                                }
                                                            >
                                                                {copiedList.includes(
                                                                    row[column.field as keyof ICustomerReturn]
                                                                ) ? (
                                                                    <CheckCircleIcon
                                                                        style={{ color: 'blue' }}
                                                                        fontSize="small"
                                                                    />
                                                                ) : (
                                                                    <FileCopyIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                )
                                        )}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </AccordionSummary>
                        <AccordionDetails className={CRT.accordionDetails}>
                            {loadingCollapse ? (
                                <Box
                                    className={CRT.collapseLoading}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%',
                                    }}
                                >
                                    <CircularProgress size={25} thickness={2} value={50} />
                                </Box>
                            ) : (
                                expandedIndex.includes(index) && (
                                    <Typography className={CRT.detailContent}>
                                        <Grid className={CRT.detailCardsGrid} container spacing={2}>
                                            {row.details &&
                                                row.details.map((detail: any, detailIndex: any) => (
                                                    <Grid
                                                        className={CRT.detailCard}
                                                        key={detailIndex}
                                                        size={{
                                                            xs: 12,
                                                            sm: 12,
                                                            md: 12,
                                                            lg: 12,
                                                            xl: 12,
                                                        }}
                                                    >
                                                        <Grid container alignItems="center">
                                                            <Grid>
                                                                <Avatar
                                                                    sx={{
                                                                        width: 110,
                                                                        height: 110,
                                                                        display: 'inline-block',
                                                                        verticalAlign: 'top',
                                                                    }}
                                                                    variant="square"
                                                                    src={detail.productImageUrl}
                                                                    className={crtCls(
                                                                        'makeAvatarImageBigger',
                                                                        CRT.inputAvatarProduct
                                                                    )}
                                                                />
                                                            </Grid>
                                                            <Grid>
                                                                <Table className={CRT.detailProductTable}>
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell>
                                                                                <h5>{t('return.barcodes')}</h5>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <h5>{t('refund.brandName')}</h5>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <h5>{t('comments.specialCode')}</h5>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <h5>{t('comments.colorCode')}</h5>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <h5>{t('refund.returnStatuKey')}</h5>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <h5>{t('refund.reasonExplanation')}</h5>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <h5>{t('refund.refundable')}</h5>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        <TableRow>
                                                                            <TableCell>{detail.barcode}</TableCell>
                                                                            <TableCell>{detail.brandName}</TableCell>
                                                                            <TableCell>{detail.ozelKod1}</TableCell>
                                                                            <TableCell>{detail.renkKod}</TableCell>
                                                                            <TableCell>
                                                                                {detail.returnItemStatusKey}
                                                                            </TableCell>
                                                                            {detail.reasonDescription && (
                                                                                <TableCell>
                                                                                    <Tooltip
                                                                                        title={
                                                                                            <h1>
                                                                                                {
                                                                                                    detail.reasonDescription
                                                                                                }
                                                                                            </h1>
                                                                                        }
                                                                                    >
                                                                                        <p>
                                                                                            {detail.reasonDescription.substring(
                                                                                                0,
                                                                                                21
                                                                                            )}{' '}
                                                                                            {detail.reasonDescription
                                                                                                .length > 21
                                                                                                ? '...'
                                                                                                : ''}
                                                                                        </p>
                                                                                    </Tooltip>
                                                                                </TableCell>
                                                                            )}
                                                                            <TableCell>
                                                                                {detail.isReturnable
                                                                                    ? t('general.yes')
                                                                                    : t('general.no')}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableBody>
                                                                </Table>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                        </Grid>
                                    </Typography>
                                )
                            )}
                        </AccordionDetails>
                    </Accordion>
                ))}
            {fetchDatas.length > 0 ? (
                <Pagination
                    className={CRT.pagination}
                    count={Math.ceil(rowCount / pageSize)}
                    page={pageNumber}
                    onChange={handlePageNumber}
                />
            ) : !loading ? (
                <NoResultFoundComponent errorText={t('refund.noReturnInformationMessage')} />
            ) : (
                <SCUILoadingLinear display={loading} />
            )}
        </Paper>
    );
};
